import { useCallback, useEffect, useState } from "react"
import { apiClient } from "@/lib/api"

interface PlatformProperty {
  id: string
  name: string
  slug: string
  tier: string
  timezone: string
  is_active: boolean
  owner_email: string
  owner_name: string
  member_count: number
  listing_count: number
  created_at: string
  updated_at: string
}

export function PlatformPropertiesPage() {
  const [properties, setProperties] = useState<PlatformProperty[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [tierFilter, setTierFilter] = useState("")
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState("")
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchProperties = useCallback(async (cursor?: string) => {
    const isLoadMore = !!cursor
    if (isLoadMore) setLoadingMore(true)
    else setLoading(true)

    try {
      const res = await apiClient.getPlatformProperties({
        search: search || undefined,
        tier: tierFilter || undefined,
        cursor: cursor || undefined,
      })
      if (res.ok && res.properties) {
        if (isLoadMore) setProperties((prev) => [...prev, ...res.properties!])
        else setProperties(res.properties)
        setHasMore(res.has_more ?? false)
        setNextCursor(res.next_cursor ?? "")
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [search, tierFilter])

  useEffect(() => { fetchProperties() }, [fetchProperties])

  return (
    <div>
      <div className="page-header">
        <h1 className="page-header__title">All Properties</h1>
        <p className="page-header__subtitle">System-wide property overview</p>
      </div>

      <div style={{ marginBottom: "var(--sf-space-4)", display: "flex", gap: "var(--sf-space-3)" }}>
        <input
          type="text"
          placeholder="Search by name, slug, or owner..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="audit-filters__select"
          style={{ flex: 1 }}
        />
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="audit-filters__select"
        >
          <option value="">All Tiers</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      <div className="section-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
            <div className="sf-spinner" />
          </div>
        ) : properties.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">No properties found</div>
            <div className="empty-state__desc">Try adjusting your search or filters.</div>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Owner</th>
                  <th>Tier</th>
                  <th>Members</th>
                  <th>Listings</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.name}</div>
                      <div style={{ fontSize: "var(--sf-text-xs)", color: "var(--sf-text-muted)" }}>
                        {p.slug}
                      </div>
                    </td>
                    <td>
                      <div>{p.owner_name || "\u2014"}</div>
                      <div style={{ fontSize: "var(--sf-text-xs)", color: "var(--sf-text-muted)" }}>
                        {p.owner_email}
                      </div>
                    </td>
                    <td style={{ textTransform: "capitalize" }}>{p.tier}</td>
                    <td>{p.member_count}</td>
                    <td>{p.listing_count}</td>
                    <td>
                      <span className={`status-pill status-pill--${p.is_active ? "healthy" : "neutral"}`}>
                        <span className="status-pill__dot" />
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div style={{ padding: "var(--sf-space-4)", textAlign: "center" }}>
                <button
                  className="data-table__action"
                  disabled={loadingMore}
                  onClick={() => fetchProperties(nextCursor)}
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
